// The 표 screen's smaller parts: the row/column menu, the sheet menu, the
// defined names, the chart form and the charts drawn over the grid, and
// what an xlsx import did. Sheets and menus take the back button first
// (useBackToClose, design 7.3).
import { useEffect, useRef, useState } from 'react'

import { errorText } from '../../api/client'
import { ErrorText, Field, Icon, IconButton, Sheet } from '../../ui/common'
import { useBackToClose } from '../../ui/router'
import type { TensorName, TensorSheet } from '../api'
import { CHART_MIN, type ChartSpec, type ChartType, chartData, chartShapes, readCharts } from '../chart'
import { HEAD_H, HEAD_W, parseRange } from '../grid/layout'
import type { GridStore } from '../grid/store'
import { TEMPLATES, type SheetTemplate } from '../templates'
import { RESERVED_NAMES, definedNameValid, sheetNameValid } from '../xlsx/model'
import { shapeText } from '../tensors'
import { nameRefusal, useShapes } from './Tensors'
import type { ViewInfo } from './GridCanvas'

export interface MenuItem {
  label: string
  onPick: () => void
  danger?: boolean
  disabled?: boolean
}

/** A small menu at a point (right click), or a sheet on a phone. */
export function PopMenu(props: { at: { x: number; y: number } | null; items: MenuItem[]; onClose: () => void; title: string; sheet: boolean }) {
  const open = props.at !== null
  const ref = useRef<HTMLDivElement>(null)
  useBackToClose(open && !props.sheet, props.onClose)
  useEffect(() => {
    if (!open || props.sheet) return
    const away = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) props.onClose()
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose()
    }
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('keydown', key)
    ref.current?.querySelector('button')?.focus()
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('keydown', key)
    }
  }, [open, props])
  const list = (
    <div className="menu g-menu-list" role="menu" aria-label={props.title}>
      {props.items.map((it) => (
        <button
          key={it.label}
          type="button"
          role="menuitem"
          className={it.danger ? 'danger' : undefined}
          disabled={it.disabled}
          onClick={() => {
            props.onClose()
            it.onPick()
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  )
  if (props.sheet)
    return (
      <Sheet open={open} onClose={props.onClose} title={props.title}>
        {list}
      </Sheet>
    )
  if (!open) return null
  return (
    <div ref={ref} className="g-pop" style={{ left: Math.min(props.at!.x, innerWidth - 220), top: Math.min(props.at!.y, innerHeight - 320) }}>
      {list}
    </div>
  )
}

/** Rename, move or delete a sheet. */
export function SheetMenu(props: { store: GridStore; sheet: TensorSheet | null; onClose: () => void; onDeleted: () => void }) {
  const { store, sheet } = props
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (sheet) {
      setName(sheet.name)
      setError(null)
    }
  }, [sheet])
  const list = store.sheets()
  const i = sheet ? list.findIndex((s) => s.id === sheet.id) : -1
  async function run(fn: () => Promise<unknown>, close = true) {
    try {
      await fn()
      if (close) props.onClose()
    } catch (e) {
      setError(errorText(e))
    }
  }
  const valid = sheetNameValid(name.trim())
  return (
    <Sheet open={sheet !== null} onClose={props.onClose} title="시트">
      {sheet && (
        <form
          className="t-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid && name.trim() !== sheet.name) void run(() => store.updateSheet(sheet.id, { name: name.trim() }))
          }}
        >
          <Field label="시트 이름" hint="31자까지, : \ / ? * [ ] 없이">
            <input value={name} maxLength={31} onChange={(e) => setName(e.target.value)} />
          </Field>
          <ErrorText text={error} />
          <div className="row-buttons">
            <button type="submit" className="primary" disabled={!valid || name.trim() === sheet.name}>
              이름 바꾸기
            </button>
          </div>
          <div className="row-buttons">
            <button
              type="button"
              className="secondary"
              disabled={i <= 0}
              onClick={() => void run(() => store.updateSheet(sheet.id, { position: i - 1 }), false)}
            >
              왼쪽으로
            </button>
            <button
              type="button"
              className="secondary"
              disabled={i < 0 || i >= list.length - 1}
              onClick={() => void run(() => store.updateSheet(sheet.id, { position: i + 1 }), false)}
            >
              오른쪽으로
            </button>
          </div>
          <button
            type="button"
            className="danger-btn"
            onClick={() => {
              if (!confirm(`'${sheet.name}' 시트를 지울까요? 칸이 모두 지워지고, 다른 시트에서 이 시트를 가리키던 수식은 #REF!가 됩니다.`)) return
              void run(async () => {
                await store.deleteSheet(sheet.id)
                props.onDeleted()
              })
            }}
          >
            시트 지우기
          </button>
        </form>
      )}
    </Sheet>
  )
}

const BUILTIN_HELP: [string, string][] = [
  ['거래', '거래 표. 열: 거래.날짜 · 거래.구분 · 거래.금액 · 거래.사람 · 거래.분류 · 거래.대분류 · 거래.결제수단 · 거래.메모'],
  ['지출', '지출[사람, 월, 분류] (분류는 대분류). 예: =SUM(지출[사람=엄마, 월="2026-10"])'],
  ['수입', '수입[사람, 월, 분류]'],
  ['예산', '예산[월, 분류]. 예: =SUM(예산[월="2026-10"])'],
  ['사람 · 월 · 분류 · 결제수단', '차원. 예: =sum(지출, over=사람)'],
  [
    'predtrend',
    '추세로 예측: predtrend(값, along=차원, horizon=개수). 예: =SUM(predtrend(지출[분류=식비], along=월))(다음 달 식비), =predtrend(B2:B13, horizon=3)',
  ],
  [
    '자르기',
    '텐서[차원=항목]. 달은 따옴표 없이도(월=2026-10, 월=2026-03..2026-06), 칸의 값으로도(=예산[월=B$1, 분류=$A2], 복사하면 엑셀처럼 따라감). A1처럼 칸 주소로 읽히는 항목은 따옴표로("A1")',
  ],
  [
    'contract · solve · inverse · det · lstsq',
    '차원 이름의 선형대수(쓰지 않는 차원은 따로 계산): contract(A, B, over=k), solve(A, b, rows=i, cols=j), inverse(A, rows=i, cols=j), det(A, rows=i, cols=j), lstsq(X, y). 예: =det(행렬, rows=행, cols=열)',
  ],
  [
    'svd · eig · qr · cholesky',
    '분해(새 차원 "성분"): svd(A, rows=i, cols=j, part="s"|"u"|"v"), eig(대칭, rows=i, cols=j, part="values"|"vectors"), qr(A, rows=i, cols=j, part="q"|"r"), cholesky(A, rows=i, cols=j)',
  ],
]

function NameRow({ store, n, shape }: { store: GridStore; n: TensorName; shape?: string }) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(n.name)
  const [formula, setFormula] = useState(n.formula)
  const [error, setError] = useState<string | null>(null)
  if (!editing)
    return (
      <li className="g-name">
        <span className="g-name-n">{n.name}</span>
        {shape && <span className="t-shape">{shape}</span>}
        <code className="g-name-f">={n.formula}</code>
        <IconButton label={`${n.name} 고치기`} onClick={() => setEditing(true)}>
          <Icon name="edit" size={18} />
        </IconButton>
        <IconButton
          label={`${n.name} 지우기`}
          onClick={() => {
            if (confirm(`이름 '${n.name}'을 지울까요? 이 이름을 쓰던 수식은 #NAME?이 됩니다.`))
              void store.deleteName(n.id).catch((e) => setError(errorText(e)))
          }}
        >
          <Icon name="trash" size={18} />
        </IconButton>
        <ErrorText text={error} />
      </li>
    )
  return (
    <li className="g-name">
      <form
        className="g-name-form"
        onSubmit={(e) => {
          e.preventDefault()
          const body: { name?: string; formula?: string } = {}
          if (name.trim() !== n.name) body.name = name.trim()
          if (formula.trim().replace(/^=/, '') !== n.formula) body.formula = formula.trim()
          if (!Object.keys(body).length) return setEditing(false)
          // computed tensors are checked on this device first (design 02 3.4)
          nameRefusal(store, name.trim(), formula, n.id)
            .then((why) => {
              if (why) return setError(why)
              return store.updateName(n.id, body).then(() => setEditing(false))
            })
            .catch((err) => setError(errorText(err)))
        }}
      >
        <input aria-label="이름" value={name} onChange={(e) => setName(e.target.value)} />
        <input aria-label="수식" value={formula} onChange={(e) => setFormula(e.target.value)} />
        <button type="submit" className="primary" disabled={!definedNameValid(name.trim()) || formula.trim() === ''}>
          저장
        </button>
        <button type="button" className="secondary" onClick={() => setEditing(false)}>
          취소
        </button>
      </form>
      <ErrorText text={error} />
    </li>
  )
}

/** The workbook's defined names (Excel's name manager) and the built-in ones. */
export function NamesSheet({ store, open, onClose }: { store: GridStore; open: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [formula, setFormula] = useState('')
  const [error, setError] = useState<string | null>(null)
  const names = store.model.nameList()
  const shapes = useShapes(store, open ? names : [])
  const reserved = RESERVED_NAMES.includes(name.trim())
  return (
    <Sheet open={open} onClose={onClose} title="이름 정의">
      <ul className="g-names">
        {names.length === 0 && <li className="muted">아직 이름이 없습니다.</li>}
        {names.map((n) => (
          <NameRow key={`${n.id}:${n.version}`} store={store} n={n} shape={(() => {
            const s = shapes?.get(n.name)
            return s ? shapeText(s) : undefined
          })()} />
        ))}
      </ul>
      <form
        className="t-form"
        onSubmit={(e) => {
          e.preventDefault()
          setError(null)
          nameRefusal(store, name.trim(), formula)
            .then((why) => {
              if (why) return setError(why)
              return store.addName(name.trim(), formula.trim()).then(() => {
                setName('')
                setFormula('')
              })
            })
            .catch((err) => setError(errorText(err)))
        }}
      >
        <Field label="새 이름" hint={reserved ? '미리 있는 이름이라 쓸 수 없어요' : '예: 세율'}>
          <input value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="수식" hint="예: =0.1 또는 =시트1!$A$1:$A$10">
          <input value={formula} onChange={(e) => setFormula(e.target.value)} />
        </Field>
        <ErrorText text={error} />
        <button type="submit" className="primary" disabled={!definedNameValid(name.trim()) || formula.trim() === ''}>
          이름 더하기
        </button>
      </form>
      <details className="g-builtins">
        <summary>미리 있는 이름과 함수</summary>
        <dl>
          {BUILTIN_HELP.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </details>
    </Sheet>
  )
}

const TYPE_LABEL: Record<ChartType, string> = { bar: '막대', line: '선', pie: '원' }

/** A new chart of a range. */
export function ChartSheet(props: { open: boolean; range: string; onClose: () => void; onCreate: (type: ChartType, range: string, title: string) => Promise<void> }) {
  const [type, setType] = useState<ChartType>('bar')
  const [range, setRange] = useState(props.range)
  const [title, setTitle] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (props.open) {
      setRange(props.range)
      setError(null)
    }
  }, [props.open, props.range])
  const ok = parseRange(range) !== null && range.includes(':')
  return (
    <Sheet open={props.open} onClose={props.onClose} title="차트">
      <form
        className="t-form"
        onSubmit={(e) => {
          e.preventDefault()
          props
            .onCreate(type, range.trim().toUpperCase(), title.trim())
            .then(props.onClose)
            .catch((err) => setError(errorText(err)))
        }}
      >
        <fieldset className="radio-row">
          <legend>종류</legend>
          {(['bar', 'line', 'pie'] as const).map((t) => (
            <label key={t} className="radio-choice">
              <input type="radio" name="chart-type" checked={type === t} onChange={() => setType(t)} />
              {TYPE_LABEL[t]}
            </label>
          ))}
        </fieldset>
        <Field label="범위" hint="첫 행·첫 열이 글자면 이름으로 씁니다">
          <input value={range} onChange={(e) => setRange(e.target.value)} />
        </Field>
        <Field label="제목">
          <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <ErrorText text={error} />
        <button type="submit" className="primary" disabled={!ok}>
          만들기
        </button>
      </form>
    </Sheet>
  )
}

/** The sheet's charts over the grid; on a PC they move by their top bar and resize at the corner. */
export function ChartsOverlay(props: { store: GridStore; sheet: TensorSheet; view: ViewInfo; fine: boolean; onChange: (charts: ChartSpec[]) => void }) {
  const { store, sheet, view } = props
  const charts = readCharts(sheet.charts)
  const [drag, setDrag] = useState<{ id: string; x: number; y: number; w: number; h: number } | null>(null)
  if (charts.length === 0) return null
  const start = (e: React.PointerEvent, c: ChartSpec, resize: boolean) => {
    if (!props.fine) return
    e.preventDefault()
    e.stopPropagation()
    const sx = e.clientX
    const sy = e.clientY
    let cur = { id: c.id, x: c.x, y: c.y, w: c.w, h: c.h }
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - sx
      const dy = ev.clientY - sy
      cur = resize
        ? { ...cur, w: Math.max(CHART_MIN.w, c.w + dx), h: Math.max(CHART_MIN.h, c.h + dy) }
        : { ...cur, x: Math.max(0, c.x + dx), y: Math.max(0, c.y + dy) }
      setDrag(cur)
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setDrag(null)
      props.onChange(charts.map((x) => (x.id === c.id ? { ...x, x: Math.round(cur.x), y: Math.round(cur.y), w: Math.round(cur.w), h: Math.round(cur.h) } : x)))
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  return (
    <>
      {charts.map((c0) => {
        const c = drag?.id === c0.id ? { ...c0, ...drag } : c0
        const r = parseRange(c.range)
        const data = r ? chartData(store.valuesOf(sheet.id, r.r1, r.c1, r.r2, r.c2).map((row) => row.map((v) => (typeof v === 'object' && v !== null ? null : v))), c.by) : { labels: [], series: [] }
        const shapes = chartShapes(c.type, data, c.w, c.h - 24, c.title)
        return (
          <div
            key={c.id}
            className="g-chart g-overlay-item"
            style={{ left: HEAD_W + c.x - view.sx, top: HEAD_H + c.y - view.sy, width: c.w, height: c.h }}
            data-chart={c.title || c.range}
          >
            <div className="g-chart-bar" onPointerDown={(e) => start(e, c0, false)}>
              <span>{c.title || `${TYPE_LABEL[c.type]} 차트 ${c.range}`}</span>
              <IconButton label="차트 지우기" onClick={() => props.onChange(charts.filter((x) => x.id !== c.id))}>
                <Icon name="close" size={16} />
              </IconButton>
            </div>
            <svg width={c.w} height={c.h - 24} role="img" aria-label={c.title || '차트'}>
              {shapes.map((s, i) => {
                const a = s.attrs as Record<string, string | number>
                if (s.kind === 'text')
                  return (
                    <text key={i} {...a}>
                      {s.text}
                    </text>
                  )
                if (s.kind === 'rect') return <rect key={i} {...a} />
                if (s.kind === 'line') return <line key={i} {...a} />
                if (s.kind === 'circle') return <circle key={i} {...a} />
                return <path key={i} {...a} />
              })}
            </svg>
            {props.fine && <div className="g-chart-size" onPointerDown={(e) => start(e, c0, true)} />}
          </div>
        )
      })}
    </>
  )
}

/** What an import did. */
export function ImportResult(props: { result: { sheets: string[]; names: number; skipped: string[] } | null; onClose: () => void }) {
  const r = props.result
  return (
    <Sheet open={r !== null} onClose={props.onClose} title="xlsx 가져오기">
      {r && (
        <div className="t-form">
          <p>
            시트 {r.sheets.length}개({r.sheets.join(', ')})
            {r.names ? `와 이름 ${r.names}개` : ''}를 가져왔습니다.
          </p>
          {r.skipped.length > 0 && (
            <>
              <p className="muted">가져오지 않은 것:</p>
              <ul className="g-skipped">
                {r.skipped.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </>
          )}
          <button type="button" className="primary" onClick={props.onClose}>
            확인
          </button>
        </div>
      )}
    </Sheet>
  )
}

/** A new sheet: empty, or from a template (templates.ts). */
export function NewSheetSheet(props: { open: boolean; onClose: () => void; onBlank: () => void; onTemplate: (t: SheetTemplate) => void }) {
  return (
    <Sheet open={props.open} onClose={props.onClose} title="새 시트">
      <div className="menu">
        <button
          type="button"
          onClick={() => {
            props.onClose()
            props.onBlank()
          }}
        >
          <strong>빈 시트</strong>
        </button>
        <p className="muted small">서식에서 고르기</p>
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            className="g-template"
            onClick={() => {
              props.onClose()
              props.onTemplate(t)
            }}
          >
            <strong>{t.title}</strong>
            <span className="muted small">{t.description}</span>
          </button>
        ))}
      </div>
    </Sheet>
  )
}
